import { useState } from "react";
import { actions } from "../store";
import type { NaukriListing } from "../types";

/**
 * Parse pasted Naukri text into listings. Accepts one company per line, or
 * "Company — Job Title" / "Company - Title" / "Company, Title" / tab-separated.
 * Lines with only a company use the current role as the title fallback.
 */
function parseNaukriPaste(text: string, roleFallback: string): NaukriListing[] {
  const seen = new Set<string>();
  const out: NaukriListing[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/\s*(?:—|–|\t|\||,| - )\s*/);
    const company = (parts[0] ?? "").trim();
    if (!company) continue;
    const key = company.toLowerCase();
    if (seen.has(key)) continue; // one discovery per company
    seen.add(key);
    const title = (parts.slice(1).join(" ").trim() || roleFallback).trim();
    out.push({ company, title });
  }
  return out;
}

export function CommandBar() {
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("AI Engineer");
  const [busy, setBusy] = useState(false);
  const [naukriOpen, setNaukriOpen] = useState(false);
  const [naukriText, setNaukriText] = useState("");

  async function discover() {
    if (!company.trim() || busy) return;
    setBusy(true);
    try {
      await actions.discover(
        { companyName: company.trim(), roleQuery: role.trim() || "Engineer" },
        "user",
      );
      setCompany("");
    } catch {
      /* surfaced in the activity feed */
    } finally {
      setBusy(false);
    }
  }

  async function importNaukri() {
    const listings = parseNaukriPaste(naukriText, role.trim() || "Engineer");
    if (!listings.length || busy) return;
    setBusy(true);
    try {
      // Same path the in-browser agent drives: hand over the companies seen on
      // the Naukri page, resolve each one's OFFICIAL portal, load all openings.
      await actions.importFromNaukri(
        { roleQuery: role.trim() || "Engineer", listings },
        "user",
      );
      setNaukriText("");
      setNaukriOpen(false);
    } catch {
      /* surfaced in the activity feed */
    } finally {
      setBusy(false);
    }
  }

  const parsedCount = parseNaukriPaste(naukriText, role.trim() || "Engineer").length;

  return (
    <div className="panel flex flex-col gap-2.5 p-3">
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <div className="flex flex-1 flex-col gap-2.5 sm:flex-row">
          <label className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-ink/60 px-3 focus-within:border-accent">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
              company
            </span>
            <input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && discover()}
              placeholder="HP, Stripe, Nvidia…"
              className="w-full bg-transparent py-2 text-sm text-text placeholder:text-faint focus:outline-none"
            />
          </label>
          <label className="flex flex-1 items-center gap-2 rounded-lg border border-border bg-ink/60 px-3 focus-within:border-accent">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
              role
            </span>
            <input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && discover()}
              placeholder="AI Engineer"
              className="w-full bg-transparent py-2 text-sm text-text placeholder:text-faint focus:outline-none"
            />
          </label>
        </div>
        <div className="flex gap-2.5">
          <button
            onClick={() => setNaukriOpen((v) => !v)}
            disabled={busy}
            title="Paste companies from a Naukri results page, then load each one's official openings"
            className={`btn-ghost sm:w-auto ${naukriOpen ? "border-accent text-accent" : ""}`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            From Naukri
          </button>
          <button onClick={discover} disabled={busy || !company.trim()} className="btn-primary sm:w-auto">
            {busy ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Working…
              </>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Discover openings
              </>
            )}
          </button>
        </div>
      </div>

      {naukriOpen && (
        <div className="animate-slide-in flex flex-col gap-2 rounded-lg border border-border bg-ink/40 p-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
              Paste from Naukri — one company per line
            </span>
            <a
              href={`https://www.naukri.com/${(role.trim() || "engineer")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")}-jobs`}
              target="_blank"
              rel="noreferrer noopener"
              className="font-mono text-[10px] text-accent hover:underline"
            >
              open Naukri ↗
            </a>
          </div>
          <textarea
            value={naukriText}
            onChange={(e) => setNaukriText(e.target.value)}
            rows={4}
            placeholder={"Nvidia\nStripe\nAtlassian — Machine Learning Engineer"}
            className="scroll-slim w-full resize-y rounded-lg border border-border bg-ink/60 p-2.5 font-mono text-[12px] text-text placeholder:text-faint focus:border-accent focus:outline-none"
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-faint">
              {parsedCount > 0
                ? `${parsedCount} compan${parsedCount === 1 ? "y" : "ies"} → will load each one's official openings`
                : "Naukri only gives the company name; the app finds each one's real career page."}
            </span>
            <button
              onClick={importNaukri}
              disabled={busy || parsedCount === 0}
              className="btn-primary px-3 py-1.5 text-xs"
            >
              {busy ? "Working…" : `Import ${parsedCount || ""}`.trim()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
